import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios, { isAxiosError } from 'axios';

interface FormFieldDef {
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[] | null;
  placeholder?: string | null;
}

interface PublicBranding {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  themeTokensJson: string | null;
}

interface PublicForm {
  title: string;
  introText: string | null;
  thankYouText: string | null;
  fieldConfig: FormFieldDef[];
  tenantBranding: PublicBranding | null;
}

const STYLE_ID = 'kairos-public-tenant-theme';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ad_id',
  'ad_set_id',
] as const;

/**
 * Story 48.4a — public lead capture landing page.
 *
 * Mounted outside the AppShell so there's no sidebar, no auth. Reads the
 * form by its public token, renders the configured fields, applies the
 * tenant's branding tokens, and submits to the public capture endpoint.
 *
 * UTM / ad_id / ad_set_id parameters from the URL query string are
 * forwarded automatically with the submission so the resulting lead
 * carries source attribution per story 48.1.
 */
export function PublicLeadFormPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<PublicForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);

  // Apply tenant branding to :root so the page picks up Cream / Coral / etc.
  useEffect(() => {
    if (!form?.tenantBranding) return;
    const branding = form.tenantBranding;
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    if (branding.themeTokensJson) {
      try {
        const tokens = JSON.parse(branding.themeTokensJson) as {
          light?: Record<string, string>;
          dark?: Record<string, string>;
        };
        const blocks: string[] = [];
        if (tokens.light) {
          blocks.push(
            `:root { ${Object.entries(tokens.light)
              .map(([k, v]) => `${k}: ${v};`)
              .join(' ')} }`,
          );
        }
        if (tokens.dark) {
          blocks.push(
            `html.dark { ${Object.entries(tokens.dark)
              .map(([k, v]) => `${k}: ${v};`)
              .join(' ')} }`,
          );
        }
        style.textContent = blocks.join('\n');
      } catch {
        /* ignore malformed tokens */
      }
    }
    if (branding.faviconUrl) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel='icon']") ||
        document.createElement('link');
      link.rel = 'icon';
      link.href = branding.faviconUrl;
      document.head.appendChild(link);
    }
    return () => {
      style?.remove();
    };
  }, [form]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    axios
      .get<PublicForm>(`/api/v1/public/forms/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setForm(data);
        const initial: Record<string, string> = {};
        for (const f of data.fieldConfig) initial[f.name] = '';
        setValues(initial);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAxiosError(err) && err.response?.status === 404) {
          setLoadError(
            'This form is no longer available. The link may have been deactivated.',
          );
        } else if (isAxiosError(err) && err.response?.status === 410) {
          setLoadError(
            'This form is paused or archived and is no longer accepting submissions.',
          );
        } else {
          setLoadError('Could not load this form. Please try again later.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  function setValue(name: string, value: string) {
    setValues((prev) => ({ ...prev, [name]: value }));
    setMissingFields((prev) => prev.filter((f) => f !== name));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token || !form) return;
    setSubmitError(null);
    setMissingFields([]);
    setSubmitting(true);

    const payload: Record<string, string | undefined> = {};
    for (const field of form.fieldConfig) {
      const v = values[field.name]?.trim();
      if (v) payload[field.name] = v;
    }
    for (const key of UTM_KEYS) {
      const v = searchParams.get(key);
      if (v) payload[key] = v;
    }

    try {
      const { data } = await axios.post<{ ok: boolean; message: string | null }>(
        `/api/v1/public/forms/${token}`,
        payload,
      );
      setSubmittedMessage(data.message ?? form.thankYouText ?? 'Thank you.');
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        const data = err.response?.data as
          | { detail?: string; missingFields?: string[] }
          | undefined;
        if (status === 400 && data?.missingFields) {
          setMissingFields(data.missingFields);
          setSubmitError(`Please fill in: ${data.missingFields.join(', ')}`);
        } else if (status === 410) {
          setSubmitError(
            'This form is no longer accepting submissions. Please contact us directly.',
          );
        } else if (status === 429) {
          setSubmitError(
            'Too many attempts. Please wait a moment and try again.',
          );
        } else {
          setSubmitError(
            data?.detail ?? 'Could not submit the form. Please try again.',
          );
        }
      } else {
        setSubmitError('Could not submit the form. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{ backgroundColor: 'var(--color-surface-1)' }}
    >
      <div
        className="w-full max-w-md rounded-lg p-8"
        style={{
          backgroundColor: 'var(--color-surface-2)',
          color: 'var(--color-on-surface)',
        }}
      >
        {form?.tenantBranding?.logoUrl && (
          <div className="mb-6 flex justify-center">
            <img
              src={form.tenantBranding.logoUrl}
              alt="Logo"
              className="h-16 w-auto"
            />
          </div>
        )}

        {loading && (
          <p className="text-center text-sm" style={{ color: 'var(--color-on-surface-muted)' }}>
            Loading…
          </p>
        )}

        {loadError && (
          <p className="text-center text-sm text-destructive">{loadError}</p>
        )}

        {!loading && !loadError && form && submittedMessage && (
          <div className="space-y-3 text-center">
            <h1 className="text-2xl font-semibold">{form.title}</h1>
            <p className="text-base">{submittedMessage}</p>
          </div>
        )}

        {!loading && !loadError && form && !submittedMessage && (
          <>
            <h1 className="mb-2 text-2xl font-semibold">{form.title}</h1>
            {form.introText && (
              <p
                className="mb-6 text-sm"
                style={{ color: 'var(--color-on-surface-muted)' }}
              >
                {form.introText}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {form.fieldConfig.map((field) => (
                <FieldInput
                  key={field.name}
                  field={field}
                  value={values[field.name] ?? ''}
                  onChange={(v) => setValue(field.name, v)}
                  invalid={missingFields.includes(field.name)}
                />
              ))}
              {submitError && (
                <p className="text-sm text-destructive">{submitError}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg px-4 py-3 text-sm font-medium transition disabled:opacity-60"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-primary-foreground)',
                }}
              >
                {submitting ? 'Sending…' : 'Submit'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  invalid,
}: {
  field: FormFieldDef;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
}) {
  const baseInputCls = 'w-full rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2';
  const inputStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-surface-1)',
    color: 'var(--color-on-surface)',
    border: invalid
      ? '1px solid hsl(0, 70%, 50%)'
      : '1px solid var(--color-divider)',
  };

  return (
    <div className="space-y-1">
      <label htmlFor={field.name} className="text-sm font-medium">
        {field.label}
        {field.required && <span className="ml-1" style={{ color: 'var(--color-primary)' }}>*</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          id={field.name}
          rows={4}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={baseInputCls}
          style={inputStyle}
        />
      ) : field.type === 'dropdown' ? (
        <select
          id={field.name}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={baseInputCls}
          style={inputStyle}
        >
          <option value="">{field.placeholder ?? 'Select…'}</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.type === 'checkbox' ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            id={field.name}
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => onChange(e.target.checked ? 'true' : '')}
          />
          <span>{field.placeholder ?? field.label}</span>
        </label>
      ) : (
        <input
          id={field.name}
          type={inputType(field.type)}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={baseInputCls}
          style={inputStyle}
        />
      )}
    </div>
  );
}

function inputType(t: string): string {
  switch (t) {
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}
